import { FC, useCallback, useEffect, useRef, useState } from "react";

import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import { DashboardRoutes } from "@simplyk/common";
import { useSearchParams } from "next/navigation";
import { useRouter } from "next/router";

import { G2ButtonLogo } from "./G2ButtonLogo";
import { GiftCardBadge } from "./GiftCardBadge";

import { AmplitudeEvents } from "@/common/constants/amplitude";
import { useCurrentUserContext } from "@/common/contexts/CurrentUserContext";
import { trpc } from "@/common/helpers/trpc";
import { useAmplitude } from "@/common/hooks/amplitude/useAmplitude";
import { useMediaQuery } from "@/common/hooks/useMediaQuery";
import { useTranslate } from "@/common/hooks/useTranslate";
import { DashboardButton, DashboardIconButton } from "@/dashboard-design-system/DashboardButton";
import { DashboardDrawer } from "@/dashboard-design-system/Drawer";
import { DashboardTypography } from "@/dashboard-design-system/Typography/DashboardTypography";
import { Cross } from "@/icons/outlined";

export const G2ReviewPrompt: FC = () => {
  const { isAuthenticated, organization } = useCurrentUserContext();

  if (!isAuthenticated || !organization) {
    return null;
  }

  return <G2ReviewPromptInner />;
};

const G2ReviewPromptInner: FC = () => {
  const [isVisible, setIsVisible] = useState(false);
  const hasCheckedOnMount = useRef(false);
  const { t } = useTranslate();
  const { isSmallScreen } = useMediaQuery();

  const { organization } = useCurrentUserContext();
  const organizationId = organization?.id;
  const { logAmplitudeEvent } = useAmplitude();
  const utils = trpc.useUtils();

  const router = useRouter();
  const { mutateAsync: dismissPrompt } = trpc.dismissG2ReviewPrompt.useMutation();

  const dismiss = useCallback(async () => {
    setIsVisible(false);
    if (organizationId) {
      logAmplitudeEvent(AmplitudeEvents.G2ReviewPromptDismissed);
      await dismissPrompt({ organizationId });
    }
  }, [organizationId, dismissPrompt, logAmplitudeEvent]);

  const openG2Review = useCallback(() => {
    logAmplitudeEvent(AmplitudeEvents.G2ReviewIframeOpened);
    setIsVisible(false);
    router.push(`/o${DashboardRoutes.G2Review}`);
  }, [logAmplitudeEvent, router]);

  const forceShow = useSearchParams().get("showG2Prompt") === "true";

  useEffect(() => {
    if (forceShow) {
      setIsVisible(true);
      return;
    }

    if (!organizationId || hasCheckedOnMount.current) {
      return;
    }
    hasCheckedOnMount.current = true;

    const checkAndShow = async () => {
      const shouldShow = await utils.shouldShowG2ReviewPrompt.fetch();
      if (shouldShow === true) {
        logAmplitudeEvent(AmplitudeEvents.G2ReviewPromptShown);
        setIsVisible(true);
      }
    };

    checkAndShow();
  }, [organizationId, utils, logAmplitudeEvent, forceShow]);

  if (!isVisible) {
    return null;
  }

  const promptBody = (
    <Stack gap={2} alignItems="center">
      <GiftCardBadge amount="25" />
      <DashboardTypography variant="h6" sx={{ textAlign: "center" }}>
        {t("dashboard", "g2Review.title")}
      </DashboardTypography>
      <DashboardTypography variant="body2" vibe="text-neutral-moderate" sx={{ textAlign: "center" }}>
        {t("dashboard", "g2Review.description")}
      </DashboardTypography>
    </Stack>
  );

  const ctaButton = (
    <DashboardButton
      variant="filled"
      vibe="brand"
      size="medium"
      onClick={openG2Review}
      startIcon={<G2ButtonLogo />}
      sx={{ width: "100%" }}
    >
      {t("dashboard", "g2Review.cta")}
    </DashboardButton>
  );

  if (isSmallScreen) {
    return (
      <DashboardDrawer open={isVisible} anchor="bottom" onClose={dismiss} title={t("dashboard", "g2Review.title")}>
        <Stack gap={2}>
          <DashboardTypography variant="body2" vibe="text-neutral-moderate">
            {t("dashboard", "g2Review.description")}
          </DashboardTypography>
          {ctaButton}
        </Stack>
      </DashboardDrawer>
    );
  }

  return (
    <Box
      sx={(theme) => ({
        position: "fixed",
        bottom: theme.spacing(3),
        right: theme.spacing(3),
        zIndex: theme.zIndex.snackbar,
        width: 355,
        backgroundColor: theme.palette.surface.neutral.white,
        border: `1px solid ${theme.palette.border.brand.intense}`,
        borderRadius: theme.radius(1),
        boxShadow: theme.boxShadow.base.strong,
      })}
    >
      <Box sx={{ position: "absolute", top: 14, right: 16 }}>
        <DashboardIconButton size="small" onClick={dismiss} aria-label="close">
          <Cross />
        </DashboardIconButton>
      </Box>
      <Stack sx={{ padding: 3, paddingBottom: 0 }} alignItems="center">
        {promptBody}
      </Stack>
      <Stack sx={{ padding: 2, paddingTop: 2 }}>{ctaButton}</Stack>
    </Box>
  );
};
